import React from "react";
import { BookCopy, ChevronLeft, LogOut } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import Card from "../ui/Card";
import { cn } from "../../lib/utils";
import { User } from "../../types/user";
import { Avatar, AvatarImage } from "../ui/avatar";
import { Separator } from "../ui/separator";
import { Button } from "../ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../../services/AuthService";
import useRepoStore from "../../services/RepoService";

/**
 * Header component.
 */
interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  repositoryName?: string;
  user?: User;
  owner?: string;
}

const Header: React.FC<HeaderProps> = ({
  user,
  className,
  children,
  repositoryName,
  owner,
  ...props
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const headerClasses = "mb-4 border-b";
  const { logout } = useAuthStore();
  const { clearStore } = useRepoStore();

  const logoutHandler = async () => {
    await logout();
    clearStore();
  };

  return (
    <header className={cn(className, headerClasses)} {...props}>
      <Card className={cn("flex flex-col items-center")}>
        <div
          className={cn(
            "flex gap-2 justify-between items-center w-full max-w-screen-2xl"
          )}
        >
          <nav>
            {repositoryName && owner ? (
              <nav className={"flex gap-3 items-center"}>
                {location.pathname !== "/" && (
                  <div>
                    <Button
                      aria-label="Return to previous page"
                      onClick={() => navigate(-1)}
                      variant="ghost"
                      size="icon"
                    >
                      <ChevronLeft />
                    </Button>
                  </div>
                )}
                <Button
                  aria-label="Go to all repositories"
                  onClick={() => navigate("/")}
                  variant="outline"
                  size="sm"
                >
                  <BookCopy className="h-4 w-4 mr-1" />
                  All repositories
                </Button>

              </nav>
            ) : (
              <nav className={"flex gap-3 items-center"}>
                <span className="text-base font-semibold tracking-tight">Localizer</span>
                <Button
                  aria-label="Go to all repositories"
                  onClick={() => navigate("/")}
                  variant="outline"
                  size="sm"
                >
                  All repositories
                </Button>
              </nav>
            )}
          </nav>
          <nav>{children}</nav>
          <nav>
            {user ? (
              <Popover>
                <PopoverTrigger>
                  <div className={cn("flex gap-2 items-center")}>
                    <span>{user.displayName}</span>
                    <Avatar>
                      <AvatarImage src={user.photo} />
                    </Avatar>
                  </div>
                </PopoverTrigger>
                <PopoverContent>
                  {user.username && <p>Username: {user.username}</p>}
                  {user.name && <p>Name: {user.name}</p>}
                  {user.githubId && <p>Git number: {user.githubId}</p>}
                  <Separator />

                  <Button onClick={() => logoutHandler()} variant={"outline"}>
                    <LogOut className={cn("mr-2 h-4 w-4")} />
                    Logout
                  </Button>
                </PopoverContent>
              </Popover>
            ) : null}
          </nav>
        </div>
      </Card>
    </header>
  );
};

/**
 * Components
 */

type RepositoryPathCardProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * RepositoryPathCard component.
 */
const RepositoryPathCard: React.FC<RepositoryPathCardProps> = ({
  className,
  children,
  ...props
}) => {
  const repositoryCardClass =
    "rounded font-normal text-gray-500 bg-blue-100/30 border border-gray-200 p-2";
  return (
    <div className={cn(className, repositoryCardClass)} {...props}>
      {children}
    </div>
  );
};

export default Header;
